export default {
  title: "Instruments",
  subtitle: "Manage laboratory instruments and their configuration",
  add: "Add instrument",
  edit: "Edit",
  delete: "Delete",
  refresh: "Refresh",
  navigate: "Open instrument detail",
  add_title: "Add instrument",
  add_subtitle: "Register a new instrument at this location",
  subtitle_name: "Instrument",
  subtitle_id: "Instrument ID",
  subtitle_lis_id: "LIS ID",
  subtitle_location: "Location",
  subtitle_name_tooltip: "Instrument name",
  subtitle_id_tooltip: "System ID",
  subtitle_lis_id_tooltip: "LIS integration key",
  subtitle_location_tooltip: "Location of this instrument",
  subtitle_serial: "Serial number",
  subtitle_serial_tooltip: "Serial number or asset tag",
  subtitle_decimal_separator: "Decimal",
  subtitle_decimal_separator_tooltip: "Decimal separator for numeric results",
  subtitle_note: "Note",
  subtitle_note_tooltip: "Operator note for this instrument",
  edit_title: "Edit instrument",
  delete_title: "Delete instrument",
  delete_message: "This instrument will be permanently removed. All associated channels will also be deleted.",
  delete_dependency_checking: "Checking whether this instrument has related configuration...",
  delete_dependency_check_failed:
    "The related configuration could not be checked. Please try again before deleting this instrument.",
  delete_has_channels:
    "This instrument cannot be deleted because it still has channels configured. Please remove them first from the Channels page.",
  delete_has_tests:
    "This instrument cannot be deleted because it still has tests configured. Please remove them first from the Tests tab.",
  delete_has_panels:
    "This instrument cannot be deleted because it still has panels configured. Please remove them first from the Panels tab.",
  delete_has_sample_types:
    "This instrument cannot be deleted because it still has sample types configured. Please remove them first from the Sample Types tab.",
  delete_has_flags:
    "This instrument cannot be deleted because it still has flags configured. Please remove them first from the Flags tab.",
  delete_has_rights:
    "This instrument cannot be deleted because it still has access rights configured. Please remove them first from the Rights tab.",

  fields: {
    name: "Name",
    name_hint: "Display name for the instrument, shown across the system",
    name_placeholder: "e.g. Cobas e801, Sysmex XN-1000",
    location: "Location",
    location_hint: "Physical location where this instrument is installed",
    decimal_separator: "Decimal separator",
    decimal_separator_hint:
      "Character used as the decimal point in numeric results from this instrument. Must match the instrument's output format.",
    decimal_separator_placeholder: ". or ,",
    mapping_key: "LIS ID",
    mapping_key_hint: "External identifier used to match this instrument in the LIS/HIS",
    mapping_key_placeholder: "e.g. COBAS01",
    serial: "Serial / Tag",
    serial_hint: "Instrument serial number or physical asset tag",
    serial_placeholder: "e.g. SN-12345",
    note: "Note",
    note_hint: "Optional — visible in the instrument header and tooltips",
    note_placeholder: "e.g. Primary hematology analyzer, bench 2",
  },

  validation: {
    name_required: "Name is required",
    name_too_long: "Name must be 100 characters or fewer",
    name_taken: "An instrument with this name already exists",
    location_required: "Location is required",
    decimal_separator_required: "Decimal separator is required",
    mapping_key_too_long: "LIS ID must be 100 characters or fewer",
    mapping_key_taken: "An instrument with this LIS ID already exists",
    serial_too_long: "Serial / Tag must be 100 characters or fewer",
    note_too_long: "Note must be 500 characters or fewer",
  },

  snackbar_added: 'Instrument "{name}" added',
  snackbar_updated: 'Instrument "{name}" updated',
  snackbar_deleted: 'Instrument "{name}" deleted',
  loadFailed: "Failed to load instruments",

  onboarding: {
    configure_channel: "Configure channel",
  },

  list: {
    search_placeholder: "Search instruments",
    tab_all: "All Instruments",
    filter_location_all: "All locations",
    filter_location_tooltip: "Filter by location",
    filter_location_active_tooltip: "Filtered by: {name}",
    status_total: "{count} instrument | {count} instruments",
    navigate: "Open instrument detail",
    collapse_group: "Collapse location",
    expand_group: "Expand location",
    channels_total: "Total channels",
    channels_connected: "Connected channels",
    channels_running: "Running channels",
    channels_enabled: "Enabled channels",
    mapping_key_tooltip: "External mapping key used to identify this instrument in connected LIS/HIS systems",
    status_healthy: "Channel connected",
    status_degraded: "Channel not connected",
    status_offline: "Channel not running",
    status_no_channels: "No channel configured",
    channel_label_total: "Total",
    channel_label_connected: "Connected",
    channel_label_running: "Running",
    channel_label_enabled: "Enabled",
    metadata: {
      locations: {
        label: "Locations",
        tooltip: "Locations with configured instruments",
      },
      instruments: {
        label: "Instruments",
        tooltip: "Total configured instruments",
      },
      channels: {
        label: "Channels",
        tooltip: "Total communication channels across all instruments",
      },
      connected: {
        label: "Connected",
        tooltip: "Channels with an active connection to an instrument",
      },
      running: {
        label: "Running",
        tooltip: "Channels currently started by Iris Core",
      },
    },
    toolbar: {
      refresh: "Refresh",
    },
    columns: {
      name: "Instrument",
      mapping_key: "LIS ID",
      decimal_separator: "Decimal",
      decimal_separator_tooltip:
        "Character used as the decimal point in numeric results from this instrument. If set incorrectly, all numeric values will be misread.",
      decimal_dot: "dot",
      decimal_comma: "comma",
      channels: "Channels",
      location: "Location",
      driver: "Driver / Protocol",
      driver_hint: "Communication protocol used by this instrument's channels",
      test_count: "No. Tests",
    },
    no_channels: "No channels",
    empty: {
      title: "No Instruments Available",
      description: "No instruments are configured yet.",
    },
  },

  page_header: {
    description: "Note for this specific instrument.",
  },

  detail: {
    save: "Save Changes",
    reset: "Reset",
    toolbar: {
      refresh_tests: "Refresh",
      refresh_panels: "Refresh",
      refresh_sample_types: "Refresh",
      refresh_rights: "Refresh",
      refresh_flags: "Refresh",
      refresh_mappings: "Refresh",
      refresh_processors: "Refresh",
    },
  },

  tabs: {
    details: "Details",
    tests: "Tests",
    panels: "Panels",
    sample_types: "Sample Types",
    rights: "Rights",
    flags: "Flags",
  },

  tests_tabs: {
    tests_list: "Tests",
    mappings: "LIS Mappings",
    processors: "Processors",
  },

  test_detail: {
    description: "Note for this specific test.",
    tabs: {
      basic_data: "Basic Data",
      mapping: "LIS Mappings",
      processors: "Processors",
    },
    navigate: "Open test detail",
    save_success: "Test saved successfully",
    save_error: "Failed to save test",
    toggle_activated: "Test activated successfully",
    toggle_deactivated: "Test deactivated successfully",
    toggle_error: "Failed to update test status",
    mapping_added: "Mapping '{key}' added",
    mapping_updated: "Mapping '{key}' updated",
    mapping_deleted: "Mapping '{key}' removed",
    mapping_add_error: "Failed to add mapping. Please try again.",
    mapping_update_error: "Failed to update mapping. Please try again.",
    mapping_delete_error: "Failed to remove mapping. Please try again.",
    processor_added: "Processor #{rank} added",
    processor_updated: "Processor #{rank} updated",
    processor_deleted: "Processor #{rank} removed",
    processor_add_error: "Failed to add processor. Please try again.",
    processor_update_error: "Failed to update processor. Please try again.",
    processor_delete_error: "Failed to remove processor. Please try again.",
    delete_success: "Test deleted successfully",
    delete_error: "Failed to delete the test. Please try again.",

    delete: "Delete test",
    master_card: {
      cannot_be_ordered: "Not Orderable",
      cannot_be_ordered_hint: "This test cannot receive orders from the LIS",
      cannot_be_result: "No Results",
      cannot_be_result_hint: "This test cannot produce result values",
      created_on: "Created on",
    },
    unsaved_dialog_title: "Unsaved Changes",
    unsaved_dialog_subtitle: "You have unsaved changes.",
    unsaved_dialog_body: "Would you like to save your changes before leaving, or discard them?",
    unsaved_save_and_leave: "Save & Continue",
    unsaved_discard: "Discard Changes",
    unsaved_stay: "Stay",
    subtitle_code_tooltip: "Test code used in LIS orders",
    subtitle_name_tooltip: "Test display name",
    subtitle_id_tooltip: "System ID",
  },

  panel_detail: {
    tabs: {
      basic_data: "Basic Data",
      mapping: "LIS Mappings",
      tests: "Tests",
    },
    activate: "Activate panel",
    deactivate: "Deactivate panel",
    save: "Save Changes",
    reset: "Reset",
    delete: "Delete panel",
    save_success: "Panel saved successfully",
    save_error: "Failed to save panel",
    toggle_activated: "Panel activated successfully",
    toggle_deactivated: "Panel deactivated successfully",
    toggle_error: "Failed to update panel status",
    master_card: {
      created_on: "Created on",
    },
    subtitle_panel: "Panel",
    subtitle_id: "Panel ID",
    subtitle_code_tooltip: "Panel code used in LIS orders",
    subtitle_name_tooltip: "Panel display name",
    subtitle_id_tooltip: "System ID",
    delete_dialog: {
      title: "Delete Panel",
      subtitle: "Delete {name}",
      confirm: "Delete panel",
      warning: "This panel will be permanently removed from this instrument.",
      has_related_items: "This panel cannot be deleted while it still has LIS mappings or tests assigned.",
    },
  },

  mappings_table: {
    columns: {
      test_name: "Test",
      mapping_key: "Mapping Key",
      lis_name: "LIS Name",
    },
    empty: {
      title: "No Mappings",
      description: "There are no mappings configured for this instrument's tests.",
    },
  },

  processors_table: {
    columns: {
      test_name: "Test",
      rank: "Rank",
      active: "Active",
      type: "Type",
    },
    empty: {
      title: "No Processors",
      description: "There are no processors configured for this instrument's tests.",
    },
  },

  coming_soon: {
    panels: {
      title: "Section under development",
      description: "This section is currently being built and will be available soon.",
    },
  },

  panels: {
    title: "Panels",
    subtitle: "Test panels configured for this instrument",
    empty_title: "No panels configured",
    empty_description: "No panels have been configured for this instrument.",
    columns: {
      panel: "Panel",
      panel_name: "Panel",
      code: "Code",
      name: "Name",
      behaviour: "Behaviour",
      behaviour_column_hint:
        "How the panel expands during ordering — which member tests are included depends on this setting.",
      tests: "Tests",
      tests_count: "No. Tests",
      tests_tooltip: "Number of tests included in this panel.",
      mappings: "LIS Mappings",
      mappings_count: "No. LIS Mappings",
      mappings_tooltip: "Number of LIS identifiers configured for this panel.",
      created: "Created",
      date_time: "Date / Time",
      date_time_tooltip: "The date and time this panel was created.",
      actions: "Actions",
      open: "Open",
    },
    add_title: "Add Panel",
    add_subtitle: "Define a new test panel for this instrument.",
    subtitle_name_tooltip: "Panel display name",
    subtitle_id_tooltip: "System ID",
    edit_title: "Edit Panel",
    action_add: "Add Panel",
    action_edit: "Edit panel",
    action_delete: "Delete panel",
    navigate: "Open panel detail",
    fields: {
      code: "Code",
      code_hint: "Short identifier for this panel",
      code_placeholder: "e.g. LIPID",
      name: "Name",
      name_hint: "Full display name shown in reports and the LIS",
      name_placeholder: "e.g. Lipid Panel",
      behaviour: "Ordering Behaviour",
      status: "Status",
      activate: "Activate",
      deactivate: "Deactivate",
    },
    validation: {
      code_required: "Code is required",
      code_too_long: "Code must be 50 characters or fewer",
      code_duplicate: "A panel with this code already exists for this instrument",
      name_required: "Name is required",
      name_too_long: "Name must be 255 characters or fewer",
      name_duplicate: "A panel with this name already exists for this instrument",
    },
    delete_title: "Delete Panel",
    delete_message: "This panel will be removed from this instrument\u2019s configuration.",
    delete_blocked: "Remove all LIS mappings and test memberships first ({mappings} mappings, {tests} tests).",
    status_enabled: "Enabled",
    disabled: "Disabled",
    status_disabled: "Disabled",
    behaviour: {
      none: "Not orderable",
      single_unit: "Single unit",
      fixed: "Fixed tests list",
      joined: "Joined tests list",
    },
    behaviour_hints: {
      none: "Ignored during ordering. Use for draft or informational panels.",
      single_unit: "Orders this panel as one item instead of expanding member tests.",
      fixed: "Expands into the Iris-configured member tests marked for fixed ordering.",
      joined: "Combines adapter-provided tests with Iris members marked for joined ordering.",
    },
    snackbar_added: "Panel '{code}' added",
    snackbar_updated: "Panel '{code}' updated",
    snackbar_deleted: "Panel '{code}' deleted",
    mappings: {
      section_title: "LIS Mappings",
      empty_title: "No LIS mappings",
      empty: "No LIS mappings configured for this panel.",
      columns: {
        mapping_key: "LIS ID",
        lis_name: "LIS Name",
        date_time: "Date / Time",
        actions: "Actions",
      },
      fields: {
        mapping_key: "LIS ID",
        mapping_key_hint: "Unique key used by the LIS to identify this panel",
        mapping_key_placeholder: "e.g. FBC-01",
        lis_name: "LIS Name",
        lis_name_hint: "Display name shown in the LIS report",
        lis_name_placeholder: "e.g. Full Blood Count",
      },
      validation: {
        mapping_key_required: "Mapping key is required",
        mapping_key_too_long: "Mapping key must be 50 characters or fewer",
        mapping_key_duplicate: "A mapping with this key already exists for this panel",
        lis_name_required: "LIS name is required",
        lis_name_too_long: "LIS name must be 255 characters or fewer",
      },
      action_add: "Add Mapping",
      action_edit: "Edit Mapping",
      add_subtitle: "Map this panel to an external LIS identifier.",
      subtitle_panel: "Panel Code",
      subtitle_id: "Panel ID",
      subtitle_lis_id: "LIS ID",
      subtitle_created: "Created",
      subtitle_panel_tooltip: "Panel this mapping belongs to",
      subtitle_panel_id_tooltip: "System ID of the parent panel",
      subtitle_lis_id_tooltip: "LIS mapping key",
      subtitle_created_tooltip: "When this mapping was created",
      delete_title: "Remove LIS Mapping",
      delete_message: "This mapping will be removed from this panel\u2019s configuration.",
      snackbar_added: "Mapping \u2018{key}\u2019 added",
      snackbar_updated: "Mapping \u2018{key}\u2019 updated",
      snackbar_deleted: "Mapping \u2018{key}\u2019 removed",
      add_error: "Failed to add mapping. Please try again.",
      update_error: "Failed to update mapping. Please try again.",
      delete_error: "Failed to remove mapping. Please try again.",
    },
    tests: {
      section_title: "Tests",
      empty_title: "No tests in panel",
      empty: "No tests have been assigned to this panel.",
      single_unit_hint:
        "Single unit panels are ordered as one panel item; member tests are informational and do not expand the order.",
      columns: {
        test: "Test",
        test_name: "Test",
        test_code: "Code",
        fixed: "Fixed",
        joined: "Joined",
        include_fixed: "Fixed",
        include_joined: "Joined",
        date_time: "Date / Time",
        actions: "Actions",
      },
      test_enabled: "Enabled",
      test_disabled: "Disabled",
      fields: {
        test: "Test",
        test_hint: "Select a test from this instrument to include in the panel.",
        test_code_label: "Test Code",
        test_modifier_label: "Modifier",
        test_id_label: "Test ID",
        test_locked_hint: "The selected test cannot be changed after the panel membership is created.",
        include_when_fixed: "Include when fixed",
        include_when_fixed_hint: "Include this test when the panel uses Fixed tests list behaviour.",
        include_when_joined: "Include when joined",
        include_when_joined_hint: "Include this test when the panel uses Joined tests list behaviour.",
        include_fixed: "Include when fixed",
        include_fixed_hint: "Include this test when the panel uses Fixed tests list behaviour.",
        include_joined: "Include when joined",
        include_joined_hint: "Include this test when the panel uses Joined tests list behaviour.",
      },
      validation: {
        test_required: "Select a test",
        include_mode_required: "Enable at least one inclusion mode for this panel test.",
        include_one: "Enable at least one inclusion mode for this panel test.",
      },
      action_add: "Add Test",
      action_edit: "Edit Test Membership",
      add_subtitle: "Choose an instrument test and when it should be included by this panel.",
      subtitle_panel_tooltip: "Panel this test belongs to",
      subtitle_test_code_tooltip: "Test code used in LIS orders",
      subtitle_id_tooltip: "System ID of the test",
      subtitle_test_name_tooltip: "Test display name",
      subtitle_created_tooltip: "When this test was added to the panel",
      subtitle_created: "Created",
      included: "Included",
      not_included: "Not included",
      included_fixed: "Included for fixed panels",
      not_included_fixed: "Not included for fixed panels",
      included_joined: "Included for joined panels",
      not_included_joined: "Not included for joined panels",
      delete_title: "Remove Test from Panel",
      delete_message: "This test will no longer be included by this panel.",
      snackbar_added: "Test '{test}' added to panel",
      snackbar_updated: "Test '{test}' updated",
      snackbar_deleted: "Test '{test}' removed from panel",
      add_error: "Failed to add test to panel. Please try again.",
      update_error: "Failed to update panel test. Please try again.",
      delete_error: "Failed to remove test from panel. Please try again.",
    },
  },

  dialogs: {
    sample_types: {
      title: "Sample Types",
      subtitle: "Sample types configured for this instrument",
      empty_title: "Not configured",
      empty_description: "No sample types have been configured for this instrument.",
      status_active: "Active",
      status_inactive: "Inactive",
      status_tooltip:
        "Indicates whether this sample type is currently active and available for use on this instrument.",
      columns: {
        code: "Instrument Code",
        mapping_key: "LIS Key",
        name: "Name",
        status: "Status",
        actions: "Actions",
      },
      add_title: "Add Sample Type",
      add_subtitle: "Enter the details for the new sample type.",
      subtitle_instrument: "Instrument",
      subtitle_instrument_lis_id: "Instrument LIS ID",
      subtitle_lis_key: "Sample Type LIS ID",
      subtitle_instrument_tooltip: "Instrument this sample type is set up for",
      subtitle_instrument_lis_id_tooltip: "LIS integration key of the instrument",
      subtitle_sample_code_tooltip: "Instrument-native sample code",
      subtitle_lis_key_tooltip: "LIS code for this sample type",
      edit_title: "Edit Sample Type",
      fields: {
        code: "Instrument Code",
        code_hint: "The code the instrument sends in messages (e.g. SER, EDTA, 1)",
        code_placeholder: "e.g. SER",
        mapping_key: "LIS Key",
        mapping_key_hint: "How the LIS identifies this material — may differ from the instrument code",
        mapping_key_placeholder: "e.g. 5 or SERUM",
        name: "Name",
        name_hint: "Human-readable label shown in the UI and reports",
        name_placeholder: "e.g. Serum",
        active: "Active",
        active_hint: "This sample type is accepted and can be processed on this instrument",
        inactive: "Inactive",
        inactive_hint: "This sample type is disabled and will not be accepted by this instrument",
      },
      validation: {
        code_required: "Code is required",
        code_too_long: "Code must be 50 characters or fewer",
        mapping_key_required: "Mapping key is required",
        mapping_key_too_long: "Mapping key must be 50 characters or fewer",
        key_taken: "A sample type with this code and mapping key already exists",
        name_required: "Name is required",
        name_too_long: "Name must be 100 characters or fewer",
      },
      action_add: "Add Sample Type",
      action_edit: "Edit sample type",
      action_delete: "Remove sample type",
      delete_title: "Remove Sample Type",
      delete_message: "This sample type will be removed from this instrument\u2019s configuration.",
      snackbar_added: "Sample type '{code}' added",
      snackbar_updated: "Sample type '{code}' updated",
      snackbar_deleted: "Sample type '{code}' removed",
    },
    rights: {
      title: "Rights",
      subtitle: "User access rights for this instrument",
      empty_title: "Not configured",
      empty_description: "No access rights have been configured for this instrument.",
      access_view: "View",
      access_view_validate: "View + Validate",
      access_view_validate_edit: "View + Validate + Edit",
      role_iris_admin: "Iris Admin",
      role_iris_user: "Iris User",
      access_tooltip: "Defines what actions users in this role can perform on this instrument.",
      created_time_tooltip: "The date and time this access rights entry was created.",
      selector_user: "User",
      selector_group: "Group",
      selector_toggle_hint: "Click to switch between User and Group",
      columns: {
        user: "User",
        role: "Role",
        created_time: "Date / Time",
        access: "Level",
        actions: "Actions",
      },
      add_title: "Add Rights Entry",
      add_subtitle: "Assign an access level to a user or group.",
      subtitle_instrument: "Instrument",
      subtitle_instrument_lis_id: "Instrument LIS ID",
      subtitle_id: "ID",
      subtitle_created: "Created",
      subtitle_instrument_tooltip: "Instrument this rule applies to",
      subtitle_instrument_lis_id_tooltip: "LIS integration key of the instrument",
      subtitle_id_tooltip: "System ID of this access rule",
      subtitle_created_tooltip: "When this access rule was created",
      edit_title: "Edit Rights Entry",
      fields: {
        user: "User",
        user_hint: "Username (from OIDC preferred_username claim)",
        user_placeholder: "e.g. john.smith",
        group: "Group",
        group_hint: "Group name (from OIDC location claim)",
        group_placeholder: "e.g. lab-technicians",
        role: "Role",
        access: "Access Level",
        access_hint: "Defines what actions users in this role can perform on this instrument",
      },
      validation: {
        user_required: "Username is required",
        user_too_long: "Username must be 100 characters or fewer",
        user_taken: "A rights entry for this username already exists",
        group_required: "Group name is required",
        group_too_long: "Group name must be 100 characters or fewer",
        group_taken: "A rights entry for this group already exists",
      },
      action_add: "Add Rights Entry",
      action_edit: "Edit rights entry",
      action_delete: "Remove rights entry",
      delete_title: "Remove Rights Entry",
      delete_message_user: "This user will lose access to this instrument.",
      delete_message_group: "This group will lose access to this instrument.",
      snackbar_added: "Rights entry for '{user}' added",
      snackbar_updated: "Rights entry for '{user}' updated",
      snackbar_deleted: "Rights entry for '{user}' removed",
    },
    flags: {
      title: "Flags",
      subtitle: "Result flags configured for this instrument",
      empty_title: "Not configured",
      empty_description: "No flags have been configured for this instrument.",
      status_filter: "Filtered from publishing",
      status_no_filter: "Published normally",
      columns: {
        code: "Code",
        description: "Description",
        severity: "Severity",
        severity_tooltip:
          "Indicates the impact level of the flag — from informational notes to critical conditions requiring immediate action",
        created_time: "Date / Time",
        created_time_tooltip: "The date and time this flag was created.",
        actions: "Actions",
      },
      add_title: "Add Flag",
      add_subtitle: "Define a new result flag for this instrument.",
      subtitle_instrument: "Instrument",
      subtitle_instrument_lis_id: "Instrument LIS ID",
      subtitle_flag: "Flag Code",
      subtitle_flag_description: "Description",
      subtitle_created: "Created",
      subtitle_instrument_tooltip: "Instrument this flag belongs to",
      subtitle_instrument_lis_id_tooltip: "LIS integration key of the instrument",
      subtitle_flag_code_tooltip: "Flag code as sent by the instrument",
      subtitle_created_tooltip: "When this flag was configured",
      edit_title: "Edit Flag",
      fields: {
        code: "Code",
        code_hint: "Spaces are replaced with underscores",
        code_placeholder: "e.g. MAINTENANCE_MODE",
        description: "Description",
        description_hint: "Human-readable label shown in the interface",
        description_placeholder: "e.g. Maintenance Mode",
        flag_type: "Filtering",
        is_filter: "Filters Results",
        is_filter_hint: "Results with this flag will be held back and not reach the LIS",
        not_filter: "Published Normally",
        not_filter_hint: "Results with this flag are sent to the LIS without filtering",
        severity: "Severity",
        severity_information: "Information",
        severity_information_hint: "Informational note \u2014 no action required",
        severity_warning: "Warning",
        severity_warning_hint: "Potential issue \u2014 review recommended",
        severity_danger: "Danger",
        severity_danger_hint: "Serious issue \u2014 action required",
        severity_panic: "Panic",
        severity_panic_hint: "Critical condition \u2014 immediate action required",
      },
      validation: {
        code_required: "Code is required",
        code_too_long: "Code must be 100 characters or fewer",
        code_taken: "A flag with this code already exists",
        description_required: "Description is required",
        description_too_long: "Description must be 500 characters or fewer",
      },
      action_add: "Add Flag",
      action_edit: "Edit flag",
      action_delete: "Delete flag",
      delete_title: "Delete Flag",
      delete_message: "This flag will be removed from this instrument\u2019s configuration.",
      snackbar_added: "Flag '{code}' added",
      snackbar_updated: "Flag '{code}' updated",
      snackbar_deleted: "Flag '{code}' removed",
    },
  },
};
